import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomeOwnerDashboard.module.css';
import axios from 'axios';
import Toast from '../../components/common/Toast/Toast';
import { proposalComparisonService, preferencesService } from '../../services/api';
import guestSessionService from '../../services/guestSessionService';

import PersonalityQuestionnaire from '../../components/common/PersonalityQuestionnaire/PersonalityQuestionnaire';
import PreferencesModal from '../../components/common/PreferencesModal/PreferencesModal';

const HomeOwnerDashboard = () => {
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectLocation, setProjectLocation] = useState('');
  const [projectBudget, setProjectBudget] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [contractorDocuments, setContractorDocuments] = useState({
    contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
    contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
    contractor3: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
  });
  const [addedContractors, setAddedContractors] = useState([]);
  const [editingContractorIndex, setEditingContractorIndex] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isBehavioralNoteRecording, setIsBehavioralNoteRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [behavioralNoteRecognition, setBehavioralNoteRecognition] = useState(null);
  const [editingComparisonId, setEditingComparisonId] = useState(null);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [existingProjectDocuments, setExistingProjectDocuments] = useState([]);
  const [existingContractorDocuments, setExistingContractorDocuments] = useState({});
  const [activeContractorKey, setActiveContractorKey] = useState(null); // Track which contractor's notes are being recorded
  const [contractorKeys, setContractorKeys] = useState(['contractor1', 'contractor2', 'contractor3']); // Dynamic contractor keys
  const [collapsedContractors, setCollapsedContractors] = useState({}); // Track collapsed state for each contractor
  const [isPremiumUser, setIsPremiumUser] = useState(true); // TODO: Get from user context/API
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingComparison, setDeletingComparison] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // Layout states
  const [viewMode, setViewMode] = useState('grid-2'); // 'grid-1', 'grid-2', 'grid-3'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'

  // Initialize speech recognition for both fields
  useEffect(() => {
    // Check if browser supports speech recognition
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      // Project description recognition
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setProjectDescription(transcript);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsRecording(false);
      };
      
      recognitionInstance.onend = () => {
        setIsRecording(false);
      };
      
      setRecognition(recognitionInstance);
      
      // Behavioral notes recognition
      const behavioralNotesInstance = new SpeechRecognition();
      behavioralNotesInstance.continuous = true;
      behavioralNotesInstance.interimResults = true;
      behavioralNotesInstance.lang = 'en-US';
      
      behavioralNotesInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        setContractorDocuments(prevDocuments => ({
          ...prevDocuments,
          [activeContractorKey]: {
            ...prevDocuments[activeContractorKey],
            behavioralNotes: transcript
          }
        }));
      };
      
      behavioralNotesInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setToast({
          type: 'error',
          message: `Speech recognition error: ${event.error}`
        });
        setIsBehavioralNoteRecording(false);
      };
      
      behavioralNotesInstance.onend = () => {
        setIsBehavioralNoteRecording(false);
      };
      
      setBehavioralNoteRecognition(behavioralNotesInstance);
    }
  }, [activeContractorKey]);

  // Handle voice recording for project description
  const toggleRecording = () => {
    if (!recognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
    } else {
      // Stop the other recording if it's active
      if (isBehavioralNoteRecording && behavioralNoteRecognition) {
        behavioralNoteRecognition.stop();
        setIsBehavioralNoteRecording(false);
      }
      recognition.start();
      setIsRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Handle voice recording for behavioral notes
  const toggleBehavioralNoteRecording = () => {
    if (!behavioralNoteRecognition) {
      setToast({
        type: 'error',
        message: 'Speech recognition is not supported in your browser'
      });
      return;
    }
    
    if (!activeContractorKey) {
      setToast({
        type: 'error',
        message: 'Please select a contractor first'
      });
      return;
    }
    
    if (isBehavioralNoteRecording) {
      behavioralNoteRecognition.stop();
      setIsBehavioralNoteRecording(false);
    } else {
      // Stop the other recording if it's active
      if (isRecording && recognition) {
        recognition.stop();
        setIsRecording(false);
      }
      setActiveContractorKey(activeContractorKey); // Ensure we have the right contractor
      behavioralNoteRecognition.start();
      setIsBehavioralNoteRecording(true);
      setToast({
        type: 'info',
        message: 'Listening... Speak now'
      });
    }
  };

  // Fetch the user's existing comparisons
  useEffect(() => {
    const fetchComparisons = async () => {
      try {
        setLoading(true);
        // Use our API service to fetch comparisons
        const response = await proposalComparisonService.getUserComparisons();
        console.log('Received comparisons data:', response);
        
        if (response.data && response.data.status === 'success') {
          // Map the response data to the format expected by the component
          const mappedComparisons = response.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            data: comparison
          }));
          
          console.log('Mapped comparisons:', mappedComparisons);
          setComparisons(mappedComparisons);
        } else {
          console.error('Unexpected response format:', response);
          setComparisons([]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching comparisons:', err);
        setError('Failed to load comparisons');
        setToast({
          type: 'error',
          message: 'Failed to load your comparisons'
        });
        setLoading(false);
      }
    };

    // Auto-import guest projects without showing popup
    const autoImportGuestProjects = async () => {
      console.log('=== AUTO-IMPORT GUEST PROJECTS ===');
      const pendingMigration = localStorage.getItem('pendingGuestMigration');
      console.log('Pending migration data:', pendingMigration);
      
      if (pendingMigration) {
        try {
          const migrationInfo = JSON.parse(pendingMigration);
          console.log('Parsed migration info:', migrationInfo);
          
          if (migrationInfo.guestSessions?.length > 0) {
            console.log('Auto-importing', migrationInfo.guestSessions.length, 'guest projects');
            
            // Extract comparison IDs from guest sessions
            const comparisonIds = migrationInfo.guestSessions
              .map(session => session.comparisonId)
              .filter(id => id);
            
            if (comparisonIds.length === 0) {
              console.warn('No valid comparison IDs found in guest sessions');
              localStorage.removeItem('pendingGuestMigration');
              return;
            }
            
            console.log(`Migrating specific comparisons: [${comparisonIds.join(', ')}]`);
            
            // Import the guest projects automatically using existing API
            try {
              const response = await proposalComparisonService.migrateSpecificComparisons(comparisonIds, migrationInfo.userId);
              const result = response.data;
              
              if (result.status === 'success') {
                console.log('Auto-import successful:', result);
                
                // Remove the pending migration data
                localStorage.removeItem('pendingGuestMigration');
                
                // Clean up guest sessions from frontend storage
                for (const session of migrationInfo.guestSessions) {
                  guestSessionService.deleteGuestSession(session.id);
                  console.log(`Removed frontend session: ${session.id}`);
                }
                
                // Clean up guest session data if all sessions are migrated
                const remainingSessions = guestSessionService.getGuestSessions();
                if (remainingSessions.length === 0) {
                  localStorage.removeItem('guestSessionId');
                  sessionStorage.removeItem('guestSessionId');
                  localStorage.removeItem('guestSessionCreated');
                }
                
                // Show success toast
                const migratedCount = result.data?.migratedComparisons || 0;
                if (migratedCount > 0) {
                  setToast({
                    type: 'success',
                    message: `Successfully imported ${migratedCount} guest project${migratedCount !== 1 ? 's' : ''} to your account!`
                  });
                } else if (comparisonIds.length > 0) {
                  setToast({
                    type: 'info',
                    message: 'Guest projects were processed successfully. Some may have already been in your account.'
                  });
                }
                
                // Refresh comparisons to show the newly imported ones
                setTimeout(async () => {
                  try {
                    const updatedComparisons = await proposalComparisonService.getUserComparisons();
                    if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
                      const mappedComparisons = updatedComparisons.data.data.map(comparison => ({
                        id: comparison.id,
                        name: comparison.project_name,
                        description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
                        createdAt: comparison.created_at,
                        contractors: (comparison.contractors || []).length,
                        data: comparison
                      }));
                      setComparisons(mappedComparisons);
                    }
                  } catch (error) {
                    console.error('Error refreshing comparisons after auto-import:', error);
                  }
                }, 1000);
                
              } else {
                throw new Error(result.message || 'Failed to import guest projects');
              }
            } catch (importError) {
              console.error('Error auto-importing guest projects:', importError);
              setToast({
                type: 'error',
                message: 'There was an issue importing your guest projects. Please try again later.'
              });
              // Don't remove migration data so it can be retried
            }
          } else {
            console.log('No guest sessions to import');
            localStorage.removeItem('pendingGuestMigration');
          }
        } catch (error) {
          console.error('Error parsing pending migration data:', error);
          localStorage.removeItem('pendingGuestMigration');
        }
      } else {
        console.log('No pending migration found');
      }
      console.log('=== END AUTO-IMPORT GUEST PROJECTS ===');
    };

    fetchComparisons();
    autoImportGuestProjects();
    checkForFirstTimeUser();
  }, []);

  // Check if this is a first-time user who hasn't completed the questionnaire
  const checkForFirstTimeUser = async () => {
    try {
      // Check if this is a fresh login (not a page refresh)
      const isFreshLogin = sessionStorage.getItem('freshLogin') === 'true';
      
      if (!isFreshLogin) {
        // Not a fresh login, don't show questionnaire
        return;
      }
      
      // Clear the fresh login flag
      sessionStorage.removeItem('freshLogin');
      
      const response = await preferencesService.checkStatus();
      if (response.data.status === 'success' && !response.data.data.hasCompleted) {
        // Show questionnaire after a short delay to let the dashboard load first
        setTimeout(() => {
          setShowQuestionnaire(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error checking preferences status:', error);
      // If there's an error checking status, don't show questionnaire
      // This ensures we only rely on backend data
    }
  };

  const handleCreateComparison = () => {
    setShowCreateModal(true);
    setCurrentStep(1);
    resetForm();
  };



  const handleCloseModal = () => {
    // Stop recording if active when closing modal
    if (isRecording && recognition) {
      recognition.stop();
      setIsRecording(false);
    }
    if (isBehavioralNoteRecording && behavioralNoteRecognition) {
      behavioralNoteRecognition.stop();
      setIsBehavioralNoteRecording(false);
    }
    setShowCreateModal(false);
    setEditingComparisonId(null); // Reset the editing ID
    setIsEditingProject(false); // Reset editing mode
    resetForm();
  };

  const resetForm = () => {
    setProjectName('');
    setProjectDescription('');
    setProjectLocation('');
    setProjectBudget('');
    setSelectedDocuments([]);
    setCurrentStep(1);
    setContractorKeys(['contractor1', 'contractor2', 'contractor3']); // Reset to default 3 contractors
    setContractorDocuments({
      contractor1: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
      contractor2: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 },
      contractor3: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
    });
    setCollapsedContractors({}); // Reset collapsed states
    setEditingContractorIndex(null);
    setEditingComparisonId(null);
    setIsEditingProject(false);
    setExistingProjectDocuments([]);
    setExistingContractorDocuments({});
    setActiveContractorKey(null);
  };

  // Pagination helper functions
  const getPaginatedItems = (items, page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items, perPage) => {
    return Math.ceil(items.length / perPage);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Sort comparisons
  const sortedComparisons = [...comparisons].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.createdAt) - new Date(b.createdAt);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'newest':
      default:
        return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  // Get paginated data
  const paginatedComparisons = getPaginatedItems(sortedComparisons, currentPage, itemsPerPage);
  const totalPages = getTotalPages(sortedComparisons, itemsPerPage);

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments([...selectedDocuments, ...files]);
  };

  const removeDocument = (index) => {
    const updatedDocs = [...selectedDocuments];
    updatedDocs.splice(index, 1);
    setSelectedDocuments(updatedDocs);
  };

  const handleContractorFileUpload = (contractorKey, e) => {
    const files = Array.from(e.target.files);
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: {
        ...prev[contractorKey],
        documents: [...prev[contractorKey].documents, ...files]
      }
    }));
  };

  const removeContractorFile = (contractorKey, fileIndex) => {
    setContractorDocuments(prev => ({
      ...prev,
      [contractorKey]: {
        ...prev[contractorKey],
        documents: prev[contractorKey].documents.filter((_, index) => index !== fileIndex)
      }
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!projectName) {
        setToast({
          type: 'error',
          message: 'Please enter a project name'
        });
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  const handleSubmit = async () => {
    try {
      // Prepare contractors data from uploaded files
      const contractors = [];
      Object.entries(contractorDocuments).forEach(([key, contractorData], index) => {
        if (contractorData.documents.length > 0) {
          contractors.push({
            name: `Contractor ${index + 1}`,
            email: '',
            phone: '',
            company: '',
            behavioralNotes: contractorData.behavioralNotes,
            appearance: contractorData.appearance,
            organization: contractorData.organization,
            competency: contractorData.competency,
            documents: contractorData.documents
          });
        }
      });

      const minContractors = isPremiumUser ? 2 : 1;
      if (contractors.length < minContractors) {
        setToast({
          type: 'error',
          message: `Please upload documents for at least ${isPremiumUser ? 'two contractors' : 'one contractor'}`
        });
        return;
      }
      
      setLoading(true);
      
      // Prepare the project data from user input
      const projectData = {
        projectName,
        projectDescription,
        projectLocation,
        projectBudget,
        contractors: contractors.map(contractor => ({
          name: contractor.name,
          email: contractor.email,
          phone: contractor.phone,
          company: contractor.company,
          behavioralNotes: contractor.behavioralNotes,
          appearance: contractor.appearance,
          organization: contractor.organization,
          competency: contractor.competency
        }))
      };
      
      // Create the comparison entry in the database
      const response = await proposalComparisonService.createComparison(projectData);
      
      if (response.data && response.data.status === 'success') {
        const comparisonId = response.data.data.id;
        
        // Upload documents for each contractor
        const uploadPromises = [];
        
        for (const contractor of contractors) {
          if (contractor.documents && contractor.documents.length > 0) {
            console.log('=== FRONTEND: UPLOADING FILES ===');
            console.log('Contractor:', contractor.name);
            console.log('Documents:', contractor.documents.length);
            console.log('Project Name:', projectName);
            console.log('Company:', contractor.company);
            
            const formData = new FormData();
            formData.append('projectName', projectName);
            formData.append('contractor', contractor.name);
            formData.append('company', contractor.company || '');
            
            // Add all document files
            for (let i = 0; i < contractor.documents.length; i++) {
              console.log(`Adding file ${i + 1}:`, contractor.documents[i].name);
              formData.append('files', contractor.documents[i]);
            }
            
            console.log('FormData prepared, making upload request...');
            
            // Upload files
            uploadPromises.push(
              proposalComparisonService.uploadContractorFiles(
                comparisonId, 
                contractor.name, 
                formData
              ).then(response => {
                console.log(`Upload successful for ${contractor.name}:`, response);
                return response;
              }).catch(error => {
                console.error(`Error uploading files for ${contractor.name}:`, error);
                return null;
              })
            );
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setToast({
          type: 'success',
          message: 'Project created successfully!'
        });
        
        // Close modal and reset form
        setShowCreateModal(false);
        resetForm();
        
        // Refresh the comparisons list instead of navigating
        const updatedComparisons = await proposalComparisonService.getUserComparisons();
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            data: comparison
          })));
        }
      } else {
        throw new Error('Failed to create project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error creating project:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to create project'
      });
      setLoading(false);
    }
  };

  const handleAddContractorToExisting = async (comparisonId) => {
    try {
      if (addedContractors.length === 0) {
        setToast({
          type: 'error',
          message: 'Please add at least one contractor'
        });
        return;
      }
      
      setLoading(true);
      
      // Get the existing comparison data
      const existingComparison = comparisons.find(c => c.id === comparisonId);
      
      if (!existingComparison) {
        throw new Error('Project not found');
      }
      
      // Prepare the update data with updated contractor information from contractorDocuments
      const updatedContractors = contractorKeys.map((contractorKey, index) => {
        const contractorData = contractorDocuments[contractorKey];
        const originalContractor = addedContractors[index] || {};
        
        return {
          name: contractorData?.existingName || originalContractor.name || `Contractor ${index + 1}`,
          email: contractorData?.existingEmail || originalContractor.email || '',
          phone: contractorData?.existingPhone || originalContractor.phone || '',
          company: contractorData?.existingCompany || originalContractor.company || '',
          behavioralNotes: contractorData?.behavioralNotes || '',
          appearance: contractorData?.appearance || 0,
          organization: contractorData?.organization || 0,
          competency: contractorData?.competency || 0,
          documents: contractorData?.documents || [] // New documents to be uploaded
        };
      });
      
      const updateData = {
        projectName,
        projectDescription,
        projectLocation,
        projectBudget,
        contractors: updatedContractors
      };
      
      // Update the comparison in the database
      const response = await proposalComparisonService.updateComparison(comparisonId, updateData);
      
      if (response.data && response.data.status === 'success') {
        // Upload documents for the new contractors
        const uploadPromises = [];
        
        for (const contractor of updatedContractors) {
          if (contractor.documents && contractor.documents.length > 0) {
            console.log('=== FRONTEND: UPLOADING FILES (ADD CONTRACTOR) ===');
            console.log('Contractor:', contractor.name);
            console.log('Documents:', contractor.documents.length);
            console.log('Project Name:', projectName);
            console.log('Company:', contractor.company);
            
            const formData = new FormData();
            formData.append('projectName', projectName);
            formData.append('contractor', contractor.name);
            formData.append('company', contractor.company || ''); // Include company name for folder creation
            
            // Add all document files
            for (let i = 0; i < contractor.documents.length; i++) {
              console.log(`Adding file ${i + 1}:`, contractor.documents[i].name);
              formData.append('files', contractor.documents[i]);
            }
            
            console.log('FormData prepared, making upload request...');
            
            // Upload files
            uploadPromises.push(
              proposalComparisonService.uploadContractorFiles(
                comparisonId, 
                contractor.name, 
                formData
              ).then(response => {
                console.log(`Upload successful for ${contractor.name}:`, response);
                return response;
              }).catch(error => {
                console.error(`Error uploading files for ${contractor.name}:`, error);
                // Continue even if one upload fails
                return null;
              })
            );
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setToast({
          type: 'success',
          message: 'Contractors added successfully!'
        });
        
        // Close modal and reset form
        setShowCreateModal(false);
        resetForm();
        
        // Refresh the comparisons list
        const updatedComparisons = await proposalComparisonService.getUserComparisons();
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            data: comparison
          })));
        }
      } else {
        throw new Error('Failed to update project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error adding contractors:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to add contractors'
      });
      setLoading(false);
    }
  };

  // Fetch documents for a project when editing
  const fetchProjectDocuments = async (projectId) => {
    try {
      const response = await proposalComparisonService.getComparisonFiles(projectId);
      if (response.data && response.data.status === 'success') {
        setExistingProjectDocuments(response.data.data.files || []);
      }
    } catch (error) {
      console.error('Error fetching project documents:', error);
      setToast({
        type: 'error',
        message: 'Failed to load project documents'
      });
    }
  };

  // Fetch documents for a contractor when editing
  const fetchContractorDocuments = async (projectId, contractor) => {
    try {
      const response = await proposalComparisonService.getContractorFiles(projectId, contractor);
      if (response.data && response.data.status === 'success') {
        setExistingContractorDocuments(prev => ({
          ...prev,
          [contractor]: response.data.data.files || []
        }));
      }
    } catch (error) {
      console.error(`Error fetching documents for ${contractor}:`, error);
    }
  };

  // Delete an existing document
  const handleDeleteExistingDocument = async (fileId) => {
    try {
      setLoading(true);
      const response = await proposalComparisonService.deleteFile(fileId);
      
      if (response.data && response.data.status === 'success') {
        // Update the existing documents lists
        setExistingProjectDocuments(prev => prev.filter(doc => doc.id !== fileId));
        
        // Update contractor documents if needed
        const updatedContractorDocs = {...existingContractorDocuments};
        Object.keys(updatedContractorDocs).forEach(contractor => {
          updatedContractorDocs[contractor] = updatedContractorDocs[contractor].filter(doc => doc.id !== fileId);
        });
        setExistingContractorDocuments(updatedContractorDocs);
        
        setToast({
          type: 'success',
          message: 'Document deleted successfully'
        });
      } else {
        throw new Error('Failed to delete document');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error deleting document:', error);
      setToast({
        type: 'error',
        message: 'Failed to delete document'
      });
      setLoading(false);
    }
  };

  // Handle editing an existing project
  const handleEditProject = (comparison) => {
    // Set project data to edit
    setProjectName(comparison.name);
    setProjectDescription(comparison.description || '');
    setProjectLocation(comparison.data.project_location || '');
    setProjectBudget(comparison.data.project_budget || '');
    
    // Set up contractor keys based on existing contractors
    const existingContractors = comparison.data.contractors || [];
    const contractorKeysArray = existingContractors.map((_, index) => `contractor${index + 1}`);
    setContractorKeys(contractorKeysArray);
    
    // Set up contractorDocuments state with existing contractor data
    const contractorDocsState = {};
    existingContractors.forEach((contractor, index) => {
      const contractorKey = `contractor${index + 1}`;
      contractorDocsState[contractorKey] = {
        documents: [], // New documents to be uploaded
        behavioralNotes: contractor.behavioralNotes || '',
        appearance: contractor.appearance || 0,
        organization: contractor.organization || 0,
        competency: contractor.competency || 0,
        // Store existing contractor info for reference
        existingName: contractor.name || '',
        existingEmail: contractor.email || '',
        existingPhone: contractor.phone || '',
        existingCompany: contractor.company || ''
      };
    });
    setContractorDocuments(contractorDocsState);
    
    // Set contractors from the existing project (keep this for compatibility)
    setAddedContractors(existingContractors.map(contractor => ({
      name: contractor.name || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      company: contractor.company || '',
      behavioralNotes: contractor.behavioralNotes || '',
      appearance: contractor.appearance || 0,
      organization: contractor.organization || 0,
      competency: contractor.competency || 0,
      documents: [] // New documents to be uploaded
    })));
    
    // Store the comparison ID to use when submitting
    setEditingComparisonId(comparison.id);
    
    // Set editing mode
    setIsEditingProject(true);
    
    // Start at step 1 for project details
    setCurrentStep(1);
    setShowCreateModal(true);
    
    // Fetch existing documents
    fetchProjectDocuments(comparison.id);
    
    // Fetch documents for each contractor
    existingContractors.forEach(contractor => {
      fetchContractorDocuments(comparison.id, contractor.name);
    });
  };

  // Function to handle saving edits
  const handleSaveProjectEdits = async () => {
    try {
      const contractorCount = getUploadedContractorsCount();
      const minContractors = isPremiumUser ? 2 : 1;
      
      if (contractorCount < minContractors) {
        setToast({
          type: 'error',
          message: `Please ensure at least ${isPremiumUser ? 'two contractors have' : 'one contractor has'} documents (existing or new)`
        });
        return;
      }
      
      setLoading(true);
      
      // Prepare the update data with updated contractor information from contractorDocuments
      const updatedContractors = contractorKeys.map((contractorKey, index) => {
        const contractorData = contractorDocuments[contractorKey];
        const originalContractor = addedContractors[index] || {};
        
        return {
          name: contractorData?.existingName || originalContractor.name || `Contractor ${index + 1}`,
          email: contractorData?.existingEmail || originalContractor.email || '',
          phone: contractorData?.existingPhone || originalContractor.phone || '',
          company: contractorData?.existingCompany || originalContractor.company || '',
          behavioralNotes: contractorData?.behavioralNotes || '',
          appearance: contractorData?.appearance || 0,
          organization: contractorData?.organization || 0,
          competency: contractorData?.competency || 0,
          documents: contractorData?.documents || [] // New documents to be uploaded
        };
      });
      
      const updateData = {
        projectName,
        projectDescription,
        projectLocation,
        projectBudget,
        contractors: updatedContractors
      };
      
      // Update the project in the database
      const response = await proposalComparisonService.updateComparison(editingComparisonId, updateData);
      
      if (response.data && response.data.status === 'success') {
        // Handle new project documents if any
        if (selectedDocuments.length > 0) {
          try {
            const formData = new FormData();
            formData.append('projectName', projectName);
            
            // Add all document files
            for (let i = 0; i < selectedDocuments.length; i++) {
              formData.append('files', selectedDocuments[i]);
            }
            
            // Upload project files (using first contractor's name as placeholder)
            await proposalComparisonService.uploadContractorFiles(
              editingComparisonId, 
              "project", 
              formData
            );
          } catch (error) {
            console.error('Error uploading project files:', error);
          }
        }
        
        // Handle new contractor documents if any
        const uploadPromises = [];
        
        for (const contractor of updatedContractors) {
          // Check if we have new documents for this contractor
          const contractorIndex = updatedContractors.findIndex(c => c.name === contractor.name);
          if (contractorIndex >= 0 && updatedContractors[contractorIndex].documents && updatedContractors[contractorIndex].documents.length > 0) {
            const formData = new FormData();
            formData.append('projectName', projectName);
            formData.append('contractor', contractor.name);
            formData.append('company', contractor.company || '');
            
            // Add all document files
            for (let i = 0; i < updatedContractors[contractorIndex].documents.length; i++) {
              formData.append('files', updatedContractors[contractorIndex].documents[i]);
            }
            
            // Upload files
            uploadPromises.push(
              proposalComparisonService.uploadContractorFiles(
                editingComparisonId, 
                contractor.name, 
                formData
              ).catch(error => {
                console.error(`Error uploading files for ${contractor.name}:`, error);
                return null;
              })
            );
          }
        }
        
        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          await Promise.all(uploadPromises);
        }
        
        setToast({
          type: 'success',
          message: 'Project updated successfully!'
        });
        
        // Close modal and reset form
        setShowCreateModal(false);
        resetForm();
        setIsEditingProject(false);
        setEditingComparisonId(null);
        
        // Refresh the comparisons list
        const updatedComparisons = await proposalComparisonService.getUserComparisons();
        if (updatedComparisons.data && updatedComparisons.data.status === 'success') {
          setComparisons(updatedComparisons.data.data.map(comparison => ({
            id: comparison.id,
            name: comparison.project_name,
            description: comparison.project_description || `Project with ${(comparison.contractors || []).length} contractors`,
            createdAt: comparison.created_at,
            contractors: (comparison.contractors || []).length,
            data: comparison
          })));
        }
      } else {
        throw new Error('Failed to update project');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error updating project:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to update project'
      });
      setLoading(false);
    }
  };

  // Handle delete project
  const handleDeleteProject = (comparison) => {
    setDeletingComparison(comparison);
    setShowDeleteModal(true);
  };

  // Confirm delete project
  const confirmDeleteProject = async () => {
    if (!deletingComparison) return;
    
    try {
      setIsDeleting(true);
      
      const response = await proposalComparisonService.deleteComparison(deletingComparison.id);
      
      if (response.data && response.data.status === 'success') {
        setToast({
          type: 'success',
          message: 'Project deleted successfully!'
        });
        
        // Remove the deleted comparison from the list
        setComparisons(prevComparisons => 
          prevComparisons.filter(comp => comp.id !== deletingComparison.id)
        );
        
        // Reset pagination if needed
        const remainingComparisons = comparisons.filter(comp => comp.id !== deletingComparison.id);
        const newTotalPages = getTotalPages(remainingComparisons, itemsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }
      } else {
        throw new Error('Failed to delete project');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setToast({
        type: 'error',
        message: err.message || 'Failed to delete project'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeletingComparison(null);
    }
  };

  // Cancel delete project
  const cancelDeleteProject = () => {
    setShowDeleteModal(false);
    setDeletingComparison(null);
  };

  // Handle questionnaire completion
  const handleQuestionnaireComplete = (preferences) => {
    setShowQuestionnaire(false);
    setToast({
      type: 'success',
      message: 'Preferences saved! We\'ll use these to better match you with contractors.'
    });
  };

  // Handle questionnaire close (skip or close button)
  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
  };

  // Handle preferences modal
  const handleOpenPreferences = () => {
    setShowPreferencesModal(true);
  };

  const handleClosePreferences = () => {
    setShowPreferencesModal(false);
  };

  // Get uploaded contractors count
  const getUploadedContractorsCount = () => {
    if (isEditingProject) {
      // When editing, count contractors that either have new documents OR existing documents
      return contractorKeys.filter(contractorKey => {
        const contractor = contractorDocuments[contractorKey];
        const hasNewDocuments = contractor?.documents.length > 0;
        const hasExistingDocuments = contractor?.existingName && 
          existingContractorDocuments[contractor.existingName] && 
          existingContractorDocuments[contractor.existingName].length > 0;
        return hasNewDocuments || hasExistingDocuments;
      }).length;
    } else if (editingComparisonId && !isEditingProject) {
      // When adding contractors to existing project, count existing contractors + new contractors with documents
      const existingCount = addedContractors.length;
      const newContractorsWithDocs = Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
      return existingCount + newContractorsWithDocs;
    } else {
      // When creating new, only count contractors with new documents
      return Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
    }
  };

  // Add new contractor section (for premium users)
  const addContractorSection = () => {
    if (!isPremiumUser) return;
    
    // Calculate current total contractors (existing + new)
    const existingCount = editingComparisonId && !isEditingProject ? addedContractors.length : 0;
    const currentTotal = existingCount + contractorKeys.length;
    const maxAllowed = isPremiumUser ? 5 : 3;
    
    if (currentTotal >= maxAllowed) return;
    
    const nextContractorNumber = currentTotal + 1;
    const newContractorKey = `contractor${nextContractorNumber}`;
    
    setContractorKeys(prev => [...prev, newContractorKey]);
    setContractorDocuments(prev => ({
      ...prev,
      [newContractorKey]: { documents: [], behavioralNotes: '', appearance: 0, organization: 0, competency: 0 }
    }));
  };

  // Remove contractor section (for premium users)
  const removeContractorSection = (contractorKey) => {
    if (!isPremiumUser || contractorKeys.length <= 2) return;
    
    setContractorKeys(prev => prev.filter(key => key !== contractorKey));
    setContractorDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[contractorKey];
      return newDocs;
    });
    setCollapsedContractors(prev => {
      const newCollapsed = { ...prev };
      delete newCollapsed[contractorKey];
      return newCollapsed;
    });
  };

  // Toggle contractor section collapse
  const toggleContractorCollapse = (contractorKey) => {
    setCollapsedContractors(prev => ({
      ...prev,
      [contractorKey]: !prev[contractorKey]
    }));
  };

  // Render the create comparison modal
  const renderCreateModal = () => {
    return (
      <div className={showCreateModal ? styles.modalOverlay : styles.hidden}>
        <div className={styles.modal}>
          <div className={styles.modalHeader}>
            <h2>
              {isEditingProject 
                ? currentStep === 1 
                  ? `Edit Project Details: ${projectName}`
                  : `Edit Contractors for "${projectName}"`
                : currentStep === 1 
                  ? (editingComparisonId ? 'Add Contractors to Project' : 'Create New Comparison') 
                  : (editingComparisonId ? `Add Contractors to "${projectName}"` : 'Add Contractors')}
            </h2>
            <button className={styles.closeButton} onClick={handleCloseModal}>×</button>
          </div>
          
          <div className={styles.modalContent}>
            {currentStep === 1 ? (
              // Step 1: Project Details
              <div className={styles.formContainer}>
                <div className={styles.formGroup}>
                  <label htmlFor="projectName">Project Name*</label>
                  <input
                    type="text"
                    id="projectName"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name"
                    required
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="projectDescription">Project Description</label>
                  <div className={styles.textareaWrapper}>
                    <textarea
                      id="projectDescription"
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      placeholder="Describe your project or tap the microphone to use voice input"
                      rows={4}
                    />
                    <button 
                      type="button"
                      className={`${styles.voiceButton} ${isRecording ? styles.recording : ''}`}
                      onClick={toggleRecording}
                      title={isRecording ? "Stop recording" : "Start voice input"}
                    >
                      <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
                    </button>
                  </div>
                  {isRecording && (
                    <div className={styles.recordingIndicator}>
                      <div className={styles.pulse}></div>
                      <span>Recording... Speak now</span>
                    </div>
                  )}
                </div>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="projectLocation">Location (Zip Code)</label>
                    <input
                      type="text"
                      id="projectLocation"
                      value={projectLocation}
                      onChange={(e) => setProjectLocation(e.target.value)}
                      placeholder="Enter zip code"
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="projectBudget">Project Budget</label>
                    <input
                      type="text"
                      id="projectBudget"
                      value={projectBudget}
                      onChange={(e) => setProjectBudget(e.target.value)}
                      placeholder="Enter budget amount"
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label>Project Documents</label>
                  <div className={styles.fileUploadContainer}>
                    <input
                      type="file"
                      id="projectDocuments"
                      onChange={handleDocumentUpload}
                      multiple
                      className={styles.fileInput}
                    />
                    <label htmlFor="projectDocuments" className={styles.fileInputLabel}>
                      <span>{isEditingProject ? "Add More Files" : "Choose Files"}</span>
                    </label>
                  </div>
                  
                  {selectedDocuments.length > 0 && (
                    <div className={styles.selectedFiles}>
                      <h4>New Documents to Upload:</h4>
                      <ul>
                        {selectedDocuments.map((doc, index) => (
                          <li key={index}>
                            {doc.name}
                            <button
                              className={styles.removeButton}
                              onClick={() => removeDocument(index)}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {isEditingProject && existingProjectDocuments.length > 0 && (
                    <div className={styles.existingFiles}>
                      <h4>Existing Documents:</h4>
                      <ul>
                        {existingProjectDocuments.map((doc) => (
                          <li key={doc.id} className={styles.existingFileItem}>
                            <span className={styles.fileName}>
                              {doc.original_name || doc.file_name}
                            </span>
                            <div className={styles.fileControls}>
                              <button 
                                className={styles.fileActionButton}
                                onClick={() => window.open(`http://localhost:5000/${doc.file_path.replace(/\\/g, '/')}`, '_blank')}
                                title="View Document"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button 
                                className={`${styles.fileActionButton} ${styles.deleteButton}`}
                                onClick={() => handleDeleteExistingDocument(doc.id)}
                                title="Delete Document"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Step 2: Contractor Information
              <div>
                {/* Simple Contractor Upload Grid */}
                <div className={styles.contractorForm}>
                  <div className={styles.contractorFormHeader}>
                    <div>
                      <h3>Upload Contractor Proposals</h3>
                      <p>Upload proposals and add notes for up to {isPremiumUser ? '5' : '3'} contractors. Add behavioral notes and ratings for each.</p>
                    </div>
                    {/* Check if we can add more contractors */}
                    {(() => {
                      const existingCount = editingComparisonId && !isEditingProject ? addedContractors.length : 0;
                      const currentTotal = existingCount + contractorKeys.length;
                      const maxAllowed = isPremiumUser ? 5 : 3;
                      return currentTotal < maxAllowed;
                    })() && (
                      <button 
                        type="button"
                        className={`${styles.addContractorButton} ${!isPremiumUser ? styles.premiumDisabled : ''}`}
                        onClick={isPremiumUser ? addContractorSection : undefined}
                        disabled={!isPremiumUser}
                        title={!isPremiumUser ? "Upgrade to Premium to add more contractors" : "Add Contractor"}
                      >
                        {!isPremiumUser && <i className="fas fa-crown" style={{color: '#fbbf24', marginRight: '0.5rem'}}></i>}
                        <i className="fas fa-plus"></i> Add Contractor
                      </button>
                    )}
                  </div>
                  
                  {/* Contractor Upload Sections */}
                  <div className={styles.contractorUploadGrid}>
                    {/* Show existing contractors in summary format when adding contractors */}
                    {editingComparisonId && !isEditingProject && addedContractors.length > 0 && (
                      <div className={styles.existingContractorsSection}>
                        <h4>Existing Contractors</h4>
                        <div className={styles.existingContractorsList}>
                          {addedContractors.map((contractor, index) => (
                            <div key={`existing-${index}`} className={styles.existingContractorSummary}>
                              <div className={styles.contractorSummaryHeader}>
                                <div className={styles.contractorNumber}>
                                  {index + 1}
                                </div>
                                <div className={styles.contractorSummaryInfo}>
                                  <h5>{contractor.name || `Contractor ${index + 1}`}</h5>
                                  {contractor.company && (
                                    <p className={styles.contractorCompany}>{contractor.company}</p>
                                  )}
                                </div>
                                <div className={styles.contractorSummaryStatus}>
                                  <i className="fas fa-check-circle" style={{color: '#10b981'}}></i>
                                  <span>Files uploaded</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Show new contractor sections */}
                    {contractorKeys.map((contractorKey, index) => {
                      // Calculate the actual contractor number (existing + new)
                      const contractorNumber = editingComparisonId && !isEditingProject 
                        ? addedContractors.length + index + 1 
                        : index + 1;
                      
                      return (
                        <div key={contractorKey} className={`${styles.contractorUploadCard} ${collapsedContractors[contractorKey] ? styles.collapsed : ''}`}>
                          <div className={styles.contractorUploadHeader}>
                            <div className={styles.contractorHeaderLeft}>
                              <div className={styles.contractorNumber}>
                                {contractorNumber}
                              </div>
                              <div>
                                <h4>
                                  {isEditingProject && contractorDocuments[contractorKey]?.existingName 
                                    ? contractorDocuments[contractorKey].existingName 
                                    : `Contractor ${contractorNumber}`}
                                </h4>
                                {isEditingProject && contractorDocuments[contractorKey]?.existingCompany && (
                                  <p style={{margin: 0, fontSize: '0.85rem', color: '#64748b'}}>
                                    {contractorDocuments[contractorKey].existingCompany}
                                  </p>
                                )}
                              </div>
                              {contractorDocuments[contractorKey]?.documents.length > 0 && (
                                <div className={styles.uploadedBadge}>
                                  <i className="fas fa-check-circle"></i>
                                  {contractorDocuments[contractorKey].documents.length} new file(s)
                                </div>
                              )}
                            </div>
                            <div className={styles.contractorHeaderActions}>
                              <button
                                type="button"
                                className={styles.collapseButton}
                                onClick={() => toggleContractorCollapse(contractorKey)}
                                title={collapsedContractors[contractorKey] ? "Expand" : "Collapse"}
                              >
                                <i className={`fas ${collapsedContractors[contractorKey] ? 'fa-chevron-down' : 'fa-chevron-up'}`}></i>
                              </button>
                              {isPremiumUser && contractorKeys.length > 1 && (
                                <button
                                  type="button"
                                  className={styles.removeContractorButton}
                                  onClick={() => removeContractorSection(contractorKey)}
                                  title="Remove Contractor"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {!collapsedContractors[contractorKey] && (
                            <div className={styles.contractorContent}>
                              <div className={styles.fileUploadContainer}>
                                <input
                                  type="file"
                                  id={`${contractorKey}Documents`}
                                  onChange={(e) => handleContractorFileUpload(contractorKey, e)}
                                  multiple
                                  className={styles.fileInput}
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                                />
                                <label htmlFor={`${contractorKey}Documents`} className={styles.fileUploadDropZone}>
                                  <div className={styles.uploadIcon}>
                                    <i className="fas fa-cloud-upload-alt"></i>
                                  </div>
                                  <div className={styles.uploadText}>
                                    <span>{isEditingProject ? "Add more files or click to upload" : "Drop files here or click to upload"}</span>
                                    <small>PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
                                  </div>
                                </label>
                              </div>
                              
                              {contractorDocuments[contractorKey]?.documents.length > 0 && (
                                <div className={styles.uploadedFilesList}>
                                  {contractorDocuments[contractorKey].documents.map((file, fileIndex) => (
                                    <div key={fileIndex} className={styles.uploadedFile}>
                                      <div className={styles.fileInfo}>
                                        <i className="fas fa-file-alt"></i>
                                        <span>{file.name}</span>
                                      </div>
                                      <button
                                        className={styles.removeFileButton}
                                        onClick={() => removeContractorFile(contractorKey, fileIndex)}
                                      >
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Show existing contractor files when editing */}
                              {isEditingProject && existingContractorDocuments[contractorDocuments[contractorKey]?.existingName] && existingContractorDocuments[contractorDocuments[contractorKey]?.existingName].length > 0 && (
                                <div className={styles.existingFiles}>
                                  <h4>Existing Documents:</h4>
                                  <ul>
                                    {existingContractorDocuments[contractorDocuments[contractorKey]?.existingName].map((doc) => (
                                      <li key={doc.id} className={styles.existingFileItem}>
                                        <span className={styles.fileName}>
                                          {doc.original_name || doc.file_name}
                                        </span>
                                        <div className={styles.fileControls}>
                                          <button 
                                            className={styles.fileActionButton}
                                            onClick={() => window.open(`http://localhost:5000/${doc.file_path.replace(/\\/g, '/')}`, '_blank')}
                                            title="View Document"
                                          >
                                            <i className="fas fa-eye"></i>
                                          </button>
                                          <button 
                                            className={`${styles.fileActionButton} ${styles.deleteButton}`}
                                            onClick={() => handleDeleteExistingDocument(doc.id)}
                                            title="Delete Document"
                                          >
                                            <i className="fas fa-trash-alt"></i>
                                          </button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {/* Behavioral Notes for this contractor */}
                              <div className={styles.formGroup}>
                                <label htmlFor={`behavioralNotes${index}`}>
                                  Behavioral Notes
                                  {!isPremiumUser && (
                                    <span className={styles.premiumBadge}>
                                      <i className="fas fa-crown"></i> Premium
                                    </span>
                                  )}
                                </label>
                                <div className={`${styles.textareaWrapper} ${!isPremiumUser ? styles.premiumDisabled : ''}`}>
                                  <textarea
                                    id={`behavioralNotes${index}`}
                                    value={contractorDocuments[contractorKey]?.behavioralNotes || ''}
                                    onChange={isPremiumUser ? (e) => setContractorDocuments(prev => ({
                                      ...prev,
                                      [contractorKey]: {
                                        ...prev[contractorKey],
                                        behavioralNotes: e.target.value
                                      }
                                    })) : undefined}
                                    placeholder={isPremiumUser ? "Add notes about contractor behavior, communication, etc. or tap the microphone to use voice input" : "Upgrade to Premium to add behavioral notes"}
                                    rows={3}
                                    disabled={!isPremiumUser}
                                  />
                                  <button 
                                    type="button"
                                    className={`${styles.voiceButton} ${isBehavioralNoteRecording && activeContractorKey === contractorKey ? styles.recording : ''} ${!isPremiumUser ? styles.premiumDisabled : ''}`}
                                    onClick={isPremiumUser ? () => {
                                      setActiveContractorKey(contractorKey);
                                      toggleBehavioralNoteRecording();
                                    } : undefined}
                                    disabled={!isPremiumUser}
                                    title={!isPremiumUser ? "Upgrade to Premium for voice input" : (isBehavioralNoteRecording && activeContractorKey === contractorKey ? "Stop recording" : "Start voice input")}
                                  >
                                    <i className={`fas ${isBehavioralNoteRecording && activeContractorKey === contractorKey ? 'fa-stop' : 'fa-microphone'}`}></i>
                                  </button>
                                </div>
                                {isBehavioralNoteRecording && activeContractorKey === contractorKey && (
                                  <div className={styles.recordingIndicator}>
                                    <div className={styles.pulse}></div>
                                    <span>Recording... Speak now</span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Contractor Perception Ratings */}
                              <div className={styles.formGroup}>
                                <label>
                                  Contractor Perception Ratings
                                  {!isPremiumUser && (
                                    <span className={styles.premiumBadge}>
                                      <i className="fas fa-crown"></i> Premium
                                    </span>
                                  )}
                                </label>
                                <div className={`${styles.ratingsContainer} ${!isPremiumUser ? styles.premiumDisabled : ''}`}>
                                  <div className={styles.ratingGroup}>
                                    <label>Appearance/Professionalism</label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.appearance >= star ? styles.starActive : ''}`}
                                            onClick={isPremiumUser ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                appearance: star
                                              }
                                            })) : undefined}
                                            disabled={!isPremiumUser}
                                            type="button"
                                            title={!isPremiumUser ? "Upgrade to Premium to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.appearance >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.appearance || 0}/5</span>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.ratingGroup}>
                                    <label>Organization</label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.organization >= star ? styles.starActive : ''}`}
                                            onClick={isPremiumUser ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                organization: star
                                              }
                                            })) : undefined}
                                            disabled={!isPremiumUser}
                                            type="button"
                                            title={!isPremiumUser ? "Upgrade to Premium to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.organization >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.organization || 0}/5</span>
                                    </div>
                                  </div>
                                  
                                  <div className={styles.ratingGroup}>
                                    <label>Competency</label>
                                    <div className={styles.ratingControls}>
                                      <div className={styles.ratingStars}>
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`${styles.starButton} ${contractorDocuments[contractorKey]?.competency >= star ? styles.starActive : ''}`}
                                            onClick={isPremiumUser ? () => setContractorDocuments(prev => ({
                                              ...prev,
                                              [contractorKey]: {
                                                ...prev[contractorKey],
                                                competency: star
                                              }
                                            })) : undefined}
                                            disabled={!isPremiumUser}
                                            type="button"
                                            title={!isPremiumUser ? "Upgrade to Premium to rate contractors" : `Rate ${star} star${star !== 1 ? 's' : ''}`}
                                          >
                                            <i className={contractorDocuments[contractorKey]?.competency >= star ? 'fas fa-star' : 'far fa-star'}></i>
                                          </button>
                                        ))}
                                      </div>
                                      <span className={styles.ratingValue}>{contractorDocuments[contractorKey]?.competency || 0}/5</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className={styles.uploadSummary}>
                    <span>Contractors with proposals: <strong>{getUploadedContractorsCount()}/{editingComparisonId && !isEditingProject ? addedContractors.length + contractorKeys.length : contractorKeys.length}</strong></span>
                    <span className={styles.minimumNote}>
                      {isEditingProject 
                        ? `Ensure at least ${isPremiumUser ? 'two contractors have' : 'one contractor has'} documents (existing or new) to save changes`
                        : editingComparisonId && !isEditingProject
                          ? `Add documents for new contractors (minimum ${Math.max(0, (isPremiumUser ? 2 : 1) - addedContractors.length)} more needed)`
                          : `Upload documents for at least ${isPremiumUser ? 'two' : 'one'} contractor${isPremiumUser ? 's' : ''} to proceed`
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className={styles.modalFooter}>
            {currentStep > 1 && !editingComparisonId && !isEditingProject && (
              <button className={styles.secondaryButton} onClick={handlePreviousStep}>
                <i className="fas fa-arrow-left"></i> Back to Project Details
              </button>
            )}
            
            {isEditingProject && currentStep === 1 ? (
              <button className={styles.primaryButton} onClick={() => setCurrentStep(2)}>
                Next: Edit Contractors <i className="fas fa-arrow-right"></i>
              </button>
            ) : currentStep === 1 && !editingComparisonId && !isEditingProject ? (
              <button className={styles.primaryButton} onClick={handleNextStep}>
                Next: Add Contractors <i className="fas fa-arrow-right"></i>
              </button>
            ) : (
              <button 
                className={styles.primaryButton} 
                onClick={
                  isEditingProject 
                    ? handleSaveProjectEdits 
                    : editingComparisonId 
                      ? () => handleAddContractorToExisting(editingComparisonId) 
                      : handleSubmit
                }
                disabled={loading || (() => {
                  if (isEditingProject) {
                    return getUploadedContractorsCount() < (isPremiumUser ? 2 : 1);
                  } else if (editingComparisonId && !isEditingProject) {
                    // When adding contractors, ensure we have at least the minimum total
                    const existingCount = addedContractors.length;
                    const newContractorsWithDocs = Object.values(contractorDocuments).filter(contractor => contractor.documents.length > 0).length;
                    const totalContractors = existingCount + newContractorsWithDocs;
                    const minRequired = isPremiumUser ? 2 : 1;
                    return totalContractors < minRequired || newContractorsWithDocs === 0;
                  } else {
                    return getUploadedContractorsCount() === 0;
                  }
                })()}
              >
                {loading 
                  ? 'Submitting...' 
                  : isEditingProject 
                    ? 'Save Changes'
                    : editingComparisonId 
                      ? `Add ${getUploadedContractorsCount()} Contractor${getUploadedContractorsCount() !== 1 ? 's' : ''} to Project`
                      : `Create Project with ${getUploadedContractorsCount()} Contractor${getUploadedContractorsCount() !== 1 ? 's' : ''}`
                }
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add a useEffect for mobile optimization
  useEffect(() => {
    // Set meta viewport for better mobile display
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0';
    document.head.appendChild(viewportMeta);

    return () => {
      // Clean up when component unmounts
      document.head.removeChild(viewportMeta);
    };
  }, []);

  // Main component rendering
  return (
    <div className={styles.dashboardContainer}>


      {/* Personality Questionnaire for first-time users */}
      {showQuestionnaire && (
        <PersonalityQuestionnaire
          onComplete={handleQuestionnaireComplete}
          onClose={handleQuestionnaireClose}
        />
      )}

      {/* Preferences Modal */}
      <PreferencesModal
        isOpen={showPreferencesModal}
        onClose={handleClosePreferences}
      />

      {toast && <Toast 
        type={toast.type} 
        message={toast.message} 
        onClose={() => setToast(null)}
        className={styles.toastContainer}
      />}
      
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>Home Owner Dashboard</h1>
          <div className={styles.headerActions}>
            <button 
              className={styles.preferencesButton} 
              onClick={handleOpenPreferences}
              title="Manage Preferences"
            >
              <i className="fas fa-cog"></i>
              Preferences
            </button>
            <button className={styles.createButton} onClick={handleCreateComparison}>
              Create New Comparison
            </button>
          </div>
        </div>
      </div>
      
      <div className={styles.content}>
        {loading ? (
          <div className={styles.contentCard}>
            <div className={styles.loading}>Loading...</div>
          </div>
        ) : error ? (
          <div className={styles.contentCard}>
            <div className={styles.error}>{error}</div>
          </div>
        ) : (
          <div className={styles.contentCard}>
            <div className={styles.dashboardContent}>
              {/* Dashboard Header */}
              <div className={styles.dashboardHeader}>
                <div className={styles.dashboardTitle}>
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Your Project Comparisons
                  </h2>
                  <p>{comparisons.length} project{comparisons.length !== 1 ? 's' : ''} in your portfolio</p>
                </div>
                
                {comparisons.length > 0 && (
                  <div className={styles.dashboardControls}>
                    {/* View Mode Toggle */}
                    <div className={styles.viewModeToggle}>
                      <span className={styles.controlLabel}>View:</span>
                      <div className={styles.toggleButtons}>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-1' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-1')}
                          title="List View - Detailed"
                        >
                          <i className="fas fa-list"></i>
                        </button>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-2' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-2')}
                          title="Grid View - 2 Columns"
                        >
                          <i className="fas fa-th-large"></i>
                        </button>
                        <button
                          className={`${styles.toggleButton} ${viewMode === 'grid-3' ? styles.active : ''}`}
                          onClick={() => setViewMode('grid-3')}
                          title="Grid View - 3 Columns"
                        >
                          <i className="fas fa-th"></i>
                        </button>
                      </div>
                    </div>
                    
                    {/* Sort Options */}
                    <div className={styles.sortControls}>
                      <span className={styles.controlLabel}>Sort by:</span>
                      <select 
                        value={sortBy} 
                        onChange={(e) => setSortBy(e.target.value)}
                        className={styles.sortSelect}
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="name">Project Name</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              
              {comparisons.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateIcon}>
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                  <h3>No comparisons yet</h3>
                  <p>Create a new comparison to start evaluating contractor proposals with AI-powered insights.</p>
                  <button 
                    className={styles.createEmptyStateButton} 
                    onClick={handleCreateComparison}
                  >
                    <i className="fas fa-plus"></i>
                    Create Your First Comparison
                  </button>
                </div>
              ) : (
                <>
                  <div className={`${styles.comparisonGrid} ${styles[viewMode]} ${totalPages > 1 ? styles.paginated : ''}`}>
                    {paginatedComparisons.map(comparison => (
                      <div key={comparison.id} className={styles.comparisonCard}>
                        <div className={styles.cardHeader}>
                          <div className={styles.cardHeaderTop}>
                            <div className={styles.projectIconAndName}>
                              <div className={styles.projectIcon}>
                                <i className="fas fa-home"></i>
                              </div>
                              <h3>{comparison.name}</h3>
                            </div>
                            <div className={styles.cardMeta}>
                              <span className={styles.dateCreated}>
                                {new Date(comparison.createdAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                              <div className={styles.contractorBadge}>
                                <i className="fas fa-users"></i>
                                {comparison.contractors} contractor{comparison.contractors !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className={styles.cardContent}>
                          <p>{comparison.description || 'No description provided'}</p>
                          
                          <div className={styles.projectStats}>
                            <div className={styles.stat}>
                              <i className="fas fa-file-contract"></i>
                              <span>Proposals</span>
                              <strong>{comparison.contractors}</strong>
                            </div>
                            {comparison.data.project_budget && (
                              <div className={styles.stat}>
                                <i className="fas fa-dollar-sign"></i>
                                <span>Budget</span>
                                <strong>${comparison.data.project_budget}</strong>
                              </div>
                            )}
                            {comparison.data.project_location && (
                              <div className={styles.stat}>
                                <i className="fas fa-map-marker-alt"></i>
                                <span>Location</span>
                                <strong>{comparison.data.project_location}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className={styles.cardFooter}>
                          <button 
                            className={styles.viewButton} 
                            onClick={() => navigate(`/homeowner/comparisons/${comparison.id}`)}
                          >
                            <i className="fas fa-chart-line"></i>
                            View Analysis
                          </button>
                          
                          <div className={styles.cardActions}>
                            <button 
                              className={styles.actionButton}
                              onClick={() => handleEditProject(comparison)}
                              title="Edit Project"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              className={styles.actionButton}
                              onClick={() => {
                                // Set up the form with existing project data
                                setProjectName(comparison.name);
                                setProjectDescription(comparison.description || '');
                                setProjectLocation(comparison.data.project_location || '');
                                setProjectBudget(comparison.data.project_budget || '');
                                
                                // Get existing contractors
                                const existingContractors = comparison.data.contractors || [];
                                const existingCount = existingContractors.length;
                                
                                // Set up existing contractors data for display
                                setAddedContractors(existingContractors);
                                
                                // Calculate how many new contractor slots to show
                                const minRequired = isPremiumUser ? 2 : 1;
                                const maxAllowed = isPremiumUser ? 5 : 3;
                                const currentTotal = existingCount;
                                
                                // Show at least 1 new contractor slot, but ensure we don't exceed max
                                const newSlotsToShow = Math.min(1, maxAllowed - currentTotal);
                                const startingContractorNumber = existingCount + 1;
                                
                                // Set up contractor keys for new contractors only
                                const newContractorKeys = [];
                                for (let i = 0; i < newSlotsToShow; i++) {
                                  newContractorKeys.push(`contractor${startingContractorNumber + i}`);
                                }
                                setContractorKeys(newContractorKeys);
                                
                                // Set up contractorDocuments state for new contractors only
                                const contractorDocsState = {};
                                newContractorKeys.forEach(contractorKey => {
                                  contractorDocsState[contractorKey] = {
                                    documents: [],
                                    behavioralNotes: '',
                                    appearance: 0,
                                    organization: 0,
                                    competency: 0
                                  };
                                });
                                setContractorDocuments(contractorDocsState);
                                
                                // Store the comparison ID to use when submitting
                                setEditingComparisonId(comparison.id);
                                
                                // Go directly to the contractor form
                                setCurrentStep(2);
                                setShowCreateModal(true);
                              }}
                              title="Add Contractor"
                            >
                              <i className="fas fa-user-plus"></i>
                            </button>
                            <button 
                              className={`${styles.actionButton} ${styles.deleteAction}`}
                              onClick={() => handleDeleteProject(comparison)}
                              title="Delete Project"
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className={styles.pagination}>
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.paginationButton}
                      >
                        <i className="fas fa-chevron-left"></i>
                        Previous
                      </button>
                      
                      <div className={styles.paginationInfo}>
                        <span className={styles.pageNumbers}>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`${styles.pageNumber} ${page === currentPage ? styles.active : ''}`}
                            >
                              {page}
                            </button>
                          ))}
                        </span>
                        <span className={styles.pageInfo}>
                          Page {currentPage} of {totalPages} • {comparisons.length} total projects
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.paginationButton}
                      >
                        Next
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {renderCreateModal()}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModal}>
            <div className={styles.modalHeader}>
              <h2>Delete Project</h2>
              <button className={styles.closeButton} onClick={cancelDeleteProject}>×</button>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.deleteWarning}>
                <div className={styles.warningIcon}>
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <div className={styles.warningContent}>
                  <h3>Are you sure you want to delete this project?</h3>
                  <p>
                    <strong>"{deletingComparison?.name}"</strong> will be permanently deleted along with:
                  </p>
                  <ul className={styles.deleteList}>
                    <li><i className="fas fa-file-alt"></i> All uploaded documents</li>
                    <li><i className="fas fa-chart-bar"></i> Analysis data and comparisons</li>
                    <li><i className="fas fa-users"></i> Contractor information and ratings</li>
                    <li><i className="fas fa-folder"></i> Project folder and files</li>
                  </ul>
                  <div className={styles.warningNote}>
                    <i className="fas fa-exclamation-circle"></i>
                    <span>This action cannot be undone!</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button className={styles.cancelButton} onClick={cancelDeleteProject}>
                <i className="fas fa-times"></i> Cancel
              </button>
              <button 
                className={styles.confirmDeleteButton} 
                onClick={confirmDeleteProject}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt"></i> Delete Project
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeOwnerDashboard; 